/**
 * 系统基础数据相关的 Composable
 * @description 封装文件格式、任务类型、软件列表的获取逻辑，并在模块级进行内存缓存。
 * 这些基础数据在前端跨页面共享，不会频繁变动。
 * @since Sprint_L1b_S1_FE 任务 1.2
 */

import type { Ref } from 'vue'
import type { FileFormat, SoftwareItem, TaskTypeItem } from '@/api/baseData'
import { computed, readonly, ref, shallowRef } from 'vue'
import { baseDataApi } from '@/api/baseData'

// ============================================================
// 模块级单例缓存与状态
// 不在 Composable 函数内部声明，以确保跨次挂载/跨组件复用缓存
// ============================================================

const fileFormatsCache = shallowRef<FileFormat[]>([])
const fileFormatsLoading = ref(false)
const fileFormatsError = ref<string | null>(null)

const taskTypesCache = shallowRef<TaskTypeItem[]>([])
const taskTypesLoading = ref(false)
const taskTypesError = ref<string | null>(null)

const softwareListCache = shallowRef<SoftwareItem[]>([])
const softwareListLoading = ref(false)
const softwareListError = ref<string | null>(null)

// ============================================================
// Composables
// ============================================================

/**
 * 文件格式获取逻辑与缓存
 */
export function useFileFormats() {
  /**
   * 内部核心加载函数
   */
  async function loadData() {
    if (fileFormatsCache.value.length > 0)
      return
    if (fileFormatsLoading.value)
      return // 防止并发请求重复加载

    fileFormatsLoading.value = true
    fileFormatsError.value = null

    try {
      const res = await baseDataApi.getFileFormats()
      if (res.code === '00000') {
        fileFormatsCache.value = res.data
      }
    }
    catch (err: any) {
      fileFormatsError.value = err?.message || '获取文件格式失败'
    }
    finally {
      fileFormatsLoading.value = false
    }
  }

  // 组件调用时自动触发（已有缓存直接返回）
  loadData()

  /** 强制刷新缓存 */
  const refresh = async () => {
    fileFormatsCache.value = []
    await loadData()
  }

  /**
   * 按类别获取文件格式的计算属性工厂函数
   * 使用方式：getFormatsByCategory.value(category)
   */
  const getFormatsByCategory = computed(() => {
    return (category: string) => fileFormatsCache.value.filter(f => f.category === category)
  })

  /** 转化为下拉列表选项使用 */
  const formatOptions = computed(() => {
    return fileFormatsCache.value.map(f => ({
      label: f.name,
      value: f.code,
    }))
  })

  return {
    formats: readonly(fileFormatsCache) as Readonly<Ref<FileFormat[]>>,
    loading: readonly(fileFormatsLoading) as Readonly<Ref<boolean>>,
    error: readonly(fileFormatsError) as Readonly<Ref<string | null>>,
    refresh,
    getFormatsByCategory,
    formatOptions,
  }
}

/**
 * 任务类型获取逻辑与缓存
 */
export function useTaskTypes() {
  async function loadData() {
    if (taskTypesCache.value.length > 0)
      return
    if (taskTypesLoading.value)
      return

    taskTypesLoading.value = true
    taskTypesError.value = null

    try {
      const res = await baseDataApi.getTaskTypes()
      if (res.code === '00000') {
        taskTypesCache.value = res.data
      }
    }
    catch (err: any) {
      taskTypesError.value = err?.message || '获取任务类型失败'
    }
    finally {
      taskTypesLoading.value = false
    }
  }

  loadData()

  /** 强制刷新缓存 */
  const refresh = async () => {
    taskTypesCache.value = []
    await loadData()
  }

  /** 转化为下拉列表选项使用 */
  const taskTypeOptions = computed(() => {
    return taskTypesCache.value.map(t => ({
      label: t.name,
      value: t.code,
    }))
  })

  /** 辅助函数：根据 code 查找 */
  const getTaskTypeByCode = (code: string) => {
    return taskTypesCache.value.find(t => t.code === code)
  }

  /**
   * 辅助函数：根据模糊关键词从 keywords 数组中匹配
   */
  const matchTaskTypeByKeyword = (keyword: string) => {
    if (!keyword)
      return undefined
    const searchKey = keyword.toLowerCase()
    return taskTypesCache.value.find((t) => {
      const matchInKeywords = t.keywords?.some(k => k.toLowerCase().includes(searchKey))
      const matchInName = t.name.toLowerCase().includes(searchKey)
      const matchInCode = t.code.toLowerCase().includes(searchKey)
      return matchInKeywords || matchInName || matchInCode
    })
  }

  return {
    taskTypes: readonly(taskTypesCache) as Readonly<Ref<TaskTypeItem[]>>,
    loading: readonly(taskTypesLoading) as Readonly<Ref<boolean>>,
    error: readonly(taskTypesError) as Readonly<Ref<string | null>>,
    refresh,
    taskTypeOptions,
    getTaskTypeByCode,
    matchTaskTypeByKeyword,
  }
}

/**
 * 软件列表获取逻辑与缓存
 */
export function useSoftwareList() {
  async function loadData() {
    if (softwareListCache.value.length > 0)
      return
    if (softwareListLoading.value)
      return

    softwareListLoading.value = true
    softwareListError.value = null

    try {
      const res = await baseDataApi.getSoftwareList()
      if (res.code === '00000') {
        softwareListCache.value = res.data
      }
    }
    catch (err: any) {
      softwareListError.value = err?.message || '获取软件列表失败'
    }
    finally {
      softwareListLoading.value = false
    }
  }

  loadData()

  /** 强制刷新缓存 */
  const refresh = async () => {
    softwareListCache.value = []
    await loadData()
  }

  /** 转化为下拉列表选项使用 */
  const softwareOptions = computed(() => {
    return softwareListCache.value.map(s => ({
      label: s.name,
      value: s.id,
    }))
  })

  return {
    softwareList: readonly(softwareListCache) as Readonly<Ref<SoftwareItem[]>>,
    loading: readonly(softwareListLoading) as Readonly<Ref<boolean>>,
    error: readonly(softwareListError) as Readonly<Ref<string | null>>,
    refresh,
    softwareOptions,
  }
}
